import styled from 'styled-components';
import { SIZES } from '../../fundamentals/sizes/sizes';
import React, { forwardRef } from 'react';
import { DEFAULT_ICON_TYPE, Icon, IIconProps } from '../Icon/Icon';
import { MATERIAL_ICONS, MaterialIcon } from '../Icon/icons';
import { IGutterProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { useActionCss } from '../../fundamentals/actions';
import { useButtonResetCss } from '../Button/ButtonRoot';
import { VARIANTS, Variant, IStateSelectorProps, useColourWithStatesCss, DEFAULT_VARIANT } from '../../fundamentals/colours/variants';
import { IBadgeProps } from '../Badge/Badge';
import { BaseDiv } from '../../common/BaseElements';

export const featuredIconSizes = {
  [SIZES.X_SMALL]: SIZES.X_MICRO,
  [SIZES.SMALL]: SIZES.MICRO,
  [SIZES.MEDIUM]: SIZES.XX_SMALL,
  [SIZES.LARGE]: SIZES.X_SMALL,
  [SIZES.X_LARGE]: SIZES.X_LARGE
};

export type FeaturedIconSize = typeof SIZES.X_SMALL | typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE | typeof SIZES.X_LARGE;

export interface IFeaturedIconProps extends Omit<IStateSelectorProps, 'variant'>, Pick<IIconProps, 'type'>, IGutterProps, Pick<IBadgeProps, 'variant'> {
  size?: FeaturedIconSize;
  name?: MaterialIcon;
  style?: React.CSSProperties;
}

export const variantToIcon: Record<Variant, MaterialIcon> = {
  [VARIANTS.PRIMARY]: MATERIAL_ICONS.StarOutline,
  [VARIANTS.ACCENT]: MATERIAL_ICONS.StarOutline,
  [VARIANTS.SECONDARY]: MATERIAL_ICONS.StarOutline,
  [VARIANTS.SECONDARY_INVERTED]: MATERIAL_ICONS.StarOutline,
  [VARIANTS.SUCCESS]: MATERIAL_ICONS.CheckCircleOutline,
  [VARIANTS.INFO]: MATERIAL_ICONS.InfoOutline,
  [VARIANTS.WARNING]: MATERIAL_ICONS.WarningAmber,
  [VARIANTS.ERROR]: MATERIAL_ICONS.ErrorOutline,
  [VARIANTS.OPAQUE_BLACK]: MATERIAL_ICONS.StarOutline,
  [VARIANTS.OPAQUE_WHITE]: MATERIAL_ICONS.StarOutline,
  [VARIANTS.CLOSEBUTTONONPRIMARY]: MATERIAL_ICONS.StarOutline
};

const FeaturedIconsContainer = styled(BaseDiv)<
  Required<Pick<IFeaturedIconProps, 'variant' | 'size'>> & Pick<IFeaturedIconProps, 'cssState' | 'light' | 'disabled'> & IGutterProps
>`
  line-height: 0;

  width: ${({ theme, size }) => theme.sizes[size]};
  height: ${({ theme, size }) => theme.sizes[size]};
  border-radius: 50%;

  ${useGutterAsMarginCss()};
  ${useActionCss};

  ${useButtonResetCss};

  ${useColourWithStatesCss};
`;

export const FeaturedIcon = forwardRef<HTMLDivElement, IFeaturedIconProps>(
  ({ name, cssState, variant = DEFAULT_VARIANT, type = DEFAULT_ICON_TYPE, disabled, light, size = SIZES.LARGE, onClick, ...rest }, ref) => {
    const iconName = name ?? variantToIcon[variant];
    const { generateTestId } = useTestId(rest, `FeaturedIcon`);

    return (
      <FeaturedIconsContainer
        cssState={cssState}
        ref={ref}
        variant={variant}
        light={light}
        size={size}
        disabled={disabled}
        {...rest}
        {...generateTestId(variant)}
        onClick={disabled ? undefined : onClick}
      >
        <Icon name={iconName} size={featuredIconSizes[size]} type={type} />
      </FeaturedIconsContainer>
    );
  }
);
